import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth, ROLES, MODULES, MODULE_LABELS, ROLE_LABELS } from "../../context/AuthContext";

const UpdateAdminRole = () => {
  const { adminId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [admin, setAdmin] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  
  const allModules = Object.values(MODULES);

  useEffect(() => {
    const fetchAdmin = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/list`,
          {
            params: { search: "", page: 1, limit: 100 },
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const foundAdmin = response?.data?.data?.find((a) => a._id === adminId);
        if (foundAdmin) {
          setAdmin(foundAdmin);
          setSelectedRole(foundAdmin.role || ROLES.SUB_ADMIN);
          setSelectedPermissions(foundAdmin.permissions || []);
        } else {
          toast.error("Admin not found");
          navigate("/admin-management");
        }
      } catch (error) {
        console.error("Error fetching admin:", error);
        toast.error("Failed to fetch admin details");
        navigate("/admin-management");
      } finally {
        setLoading(false);
      }
    };

    fetchAdmin();
  }, [adminId, navigate]);

  const handlePermissionChange = (module) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(module)) {
        return prev.filter((p) => p !== module);
      } else {
        return [...prev, module];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPermissions.length === allModules.length) {
      setSelectedPermissions([]);
    } else {
      setSelectedPermissions([...allModules]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (adminId === user?._id && selectedRole !== user?.role) {
      toast.error("You cannot change your own role");
      return;
    }

    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    // Validate permissions for sub admin role
    if (selectedRole === ROLES.SUB_ADMIN && selectedPermissions.length === 0) {
      toast.error("Please select at least one module permission for Sub Admin");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        adminId,
        role: selectedRole,
      };

      // Add permissions only for sub admin role
      if (selectedRole === ROLES.SUB_ADMIN) {
        payload.permissions = selectedPermissions;
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL}/admin/update-role`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success("Admin updated successfully");
      navigate("/admin-management");
    } catch (error) {
      console.error("Error updating admin:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.response?.message ||
        "Failed to update admin";
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case ROLES.SUPER_ADMIN:
        return "badge badge-danger";
      case ROLES.SUB_ADMIN:
        return "badge badge-primary";
      default:
        return "badge badge-light";
    }
  };

  if (loading) {
    return (
      <div className="content-wrapper">
        <section className="content">
          <div className="container-fluid">
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Update Admin Role</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/admin-management">Admin Management</Link>
                </li>
                <li className="breadcrumb-item active">Update Role</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Admin Details</h3>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <strong>Name:</strong> {admin?.name}
                    {admin?._id === user?._id && (
                      <span className="badge badge-info ml-2">You</span>
                    )}
                  </div>
                  <div className="mb-3">
                    <strong>Email:</strong> {admin?.email}
                  </div>
                  <div className="mb-3">
                    <strong>Current Role:</strong>{" "}
                    <span className={getRoleBadgeClass(admin?.role)}>
                      {ROLE_LABELS[admin?.role] || admin?.role?.replace("_", " ").toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Change Role</h3>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="card-body">
                    {admin?._id === user?._id && (
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle mr-2"></i>
                        You cannot change your own role.
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="role">Select Role</label>
                      <select
                        className="form-control"
                        id="role"
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        disabled={admin?._id === user?._id}
                      >
                        <option value={ROLES.SUB_ADMIN}>Sub Admin - Custom module access</option>
                        <option value={ROLES.SUPER_ADMIN}>Super Admin - Full access + admin management</option>
                      </select>
                      <small className="text-muted">
                        {selectedRole === ROLES.SUPER_ADMIN 
                          ? "Super Admin has full access to all modules and can manage other admins."
                          : "Sub Admin will have access only to selected modules below."}
                      </small>
                    </div>

                    {/* Module Permissions - Only show for Sub Admin role */}
                    {selectedRole === ROLES.SUB_ADMIN && (
                      <div className="form-group">
                        <label>Module Permissions</label>
                        <div className="mb-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={handleSelectAll}
                            disabled={admin?._id === user?._id}
                          >
                            {selectedPermissions.length === allModules.length ? "Deselect All" : "Select All"}
                          </button>
                          <span className="ml-2 text-muted">
                            ({selectedPermissions.length} of {allModules.length} selected)
                          </span>
                        </div>
                        <div className="row">
                          {allModules.map((module) => (
                            <div className="col-md-6" key={module}>
                              <div className="form-check mb-2">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`module-${module}`}
                                  checked={selectedPermissions.includes(module)}
                                  onChange={() => handlePermissionChange(module)}
                                  disabled={admin?._id === user?._id}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`module-${module}`}
                                >
                                  {MODULE_LABELS[module]}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedRole === ROLES.SUPER_ADMIN && (
                      <div className="alert alert-info">
                        <i className="fas fa-info-circle mr-2"></i>
                        Super Admin has full access to all modules automatically.
                      </div>
                    )}
                  </div>

                  <div className="card-footer">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving || admin?._id === user?._id}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2"></span>
                          Updating...
                        </>
                      ) : (
                        "Update Role"
                      )}
                    </button>
                    <Link to="/admin-management" className="btn btn-secondary ml-2">
                      Cancel
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UpdateAdminRole;
